/**
 * When the demo asks for the sale, and what it knows when it does (SKE-4166,
 * phase 6).
 *
 * ONE AUTO-TRIGGER, ONE STANDING DOOR
 * -----------------------------------
 * The offer auto-opens at exactly one moment, and is otherwise only ever a
 * click away:
 *
 *   CLEARED    they emptied the approval queue. This is the demo's payoff beat
 *              — the same condition the queue-cleared confetti celebrates (now
 *              client-scoped in DemoPayoff) — and the ONLY moment the panel
 *              opens itself. The visitor has just experienced the outcome we
 *              are selling.
 *   ENGAGED    they made `CTA_ACTION_THRESHOLD` decisions without finishing.
 *              This no longer pops a dialog over them mid-play (the intro tour
 *              now carries the explanation); it just makes the offer REACHABLE,
 *              surfacing the floating CTA pill so a visitor who wanders off can
 *              still open it on their own.
 *
 * It opens ONCE per visit, and dismissing it is free and remembered for the
 * visit — the same "never nag" contract `useDemoGate` established for the email
 * gate. A `Reset demo` is the deliberate way back to it.
 *
 * Progress is derived from the visitor's own store through the phase-2 mirror
 * model, so the counts the CTA quotes and the counts the marketer panel shows
 * are the same numbers from the same object, and can never disagree.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { trackDemo } from './demoAnalytics'
import type { MirrorProgress } from './demoMirror'
import { mirrorProgress, toMirrorPosts } from './demoMirror'
import { getDemoStoreSnapshot, subscribeDemoStore } from './demoStore'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/** Decisions before the offer is made to a visitor who hasn't finished. */
export const CTA_ACTION_THRESHOLD = 4

/**
 * Let the queue-cleared confetti (DemoPayoff, client-scoped) read before a
 * dialog covers it. Skipped entirely under `prefers-reduced-motion`, where the
 * payoff fires no confetti and there is nothing to wait for.
 */
export const CTA_OPEN_DELAY_MS = 1400

const DISMISSED_KEY = 'sked.demo.approvals.cta-dismissed.v1'

const readDismissed = (): boolean => {
  try {
    return globalThis.window?.sessionStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (): void => {
  try {
    globalThis.window?.sessionStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Storage disabled — the in-memory state still suppresses it this visit.
  }
}

/** Why the panel opened. Drives the headline and rides on the analytics. */
export type DemoConversionTrigger = 'cleared' | 'engaged'

export interface DemoConversionState {
  isOpen: boolean
  /** The visitor has engaged enough that a persistent way back is warranted. */
  isReachable: boolean
  trigger: DemoConversionTrigger
  progress: MirrorProgress
  /** Approve + request-changes decisions taken. */
  actionCount: number
  open: () => void
  dismiss: () => void
}

export const useDemoConversion = (): DemoConversionState => {
  const store = useSyncExternalStore(subscribeDemoStore, getDemoStoreSnapshot)
  const prefersReducedMotion = usePrefersReducedMotion()

  const progress = useMemo(() => mirrorProgress(toMirrorPosts(store)), [store])

  // Status changes only. A note is engagement, but the CTA speaks about
  // approvals and its threshold should count the decisions it quotes.
  const actionCount = progress.approved + progress.requestChange

  const hasCleared =
    progress.total > 0 && progress.pending === 0 && actionCount > 0
  const trigger: DemoConversionTrigger = hasCleared ? 'cleared' : 'engaged'
  const shouldOffer = hasCleared || actionCount >= CTA_ACTION_THRESHOLD

  const [isOpen, setIsOpen] = useState(false)
  const [isDismissed, setIsDismissed] = useState(readDismissed)

  // Auto-open fires once per visit, and ONLY on a cleared queue — the payoff
  // beat. Reaching the engagement threshold no longer auto-opens; it only makes
  // the offer reachable (the floating pill) via `isReachable` below.
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (hasAutoOpenedRef.current || isDismissed || !hasCleared) {
      return
    }
    hasAutoOpenedRef.current = true

    if (prefersReducedMotion) {
      setIsOpen(true)
      return
    }

    const timer = setTimeout(() => setIsOpen(true), CTA_OPEN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isDismissed, hasCleared, prefersReducedMotion])

  // One `demo-cta-shown` per visit, whatever opened it — the KPI denominator
  // every CTA event below is measured against.
  const hasTrackedShownRef = useRef(false)
  useEffect(() => {
    if (!isOpen || hasTrackedShownRef.current) {
      return
    }
    hasTrackedShownRef.current = true
    trackDemo('demo-cta-shown', {
      trigger,
      action_count: actionCount,
      approved: progress.approved,
      request_change: progress.requestChange,
    })
  }, [isOpen, trigger, actionCount, progress.approved, progress.requestChange])

  const open = useCallback(() => setIsOpen(true), [])

  const dismiss = useCallback(() => {
    setIsOpen(false)
    if (readDismissed()) {
      return
    }
    setIsDismissed(true)
    writeDismissed()
  }, [])

  return {
    isOpen,
    // Only after the offer has been made once — a visitor who has taken two
    // actions doesn't need a permanent button advertising the sale.
    isReachable: shouldOffer,
    trigger,
    progress,
    actionCount,
    open,
    dismiss,
  }
}
